import React from 'react';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatStrikethrough,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  Undo,
  Redo,
  DeleteOutlined,
  InsertPageBreak,
  FormatColorFill,
} from '@mui/icons-material';
import { FONT_FAMILIES, FONT_SIZES } from '../constants';
import { Btn, Sep, Dropdown, ColorWrap } from './ToolbarPrimitives';

interface FmtState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
  justifyLeft: boolean;
  justifyCenter: boolean;
  justifyRight: boolean;
  justifyFull: boolean;
}

interface ToolbarProps {
  exec: (cmd: string, value?: string | null) => void;
  fmt: FmtState;
  saveSelection: () => void;
  restoreSelection: () => void;
  applyFontSizeFromToolbar: (size: number) => void;
  textColor: string;
  setTextColorState: (color: string) => void;
  hlColor: string;
  setHlColorState: (color: string) => void;
  fontFamily: string;
  fontSize: string;
  inTable: boolean;
  cellBgColor: string;
  applyCellBg: (color: string) => void;
  insertPageBreak: () => void;
  deleteCurrentPage: () => void;
  editorPageCount: number;
  insertRowAbove: () => void;
  insertRowBelow: () => void;
  deleteRow: () => void;
}

const FONT_SIZE_OPTIONS = [{ label: 'Size', value: '' }, ...FONT_SIZES.map(s => ({ label: String(s), value: String(s) }))];

export const Toolbar = ({
  exec,
  fmt,
  saveSelection,
  restoreSelection,
  applyFontSizeFromToolbar,
  textColor,
  setTextColorState,
  hlColor,
  setHlColorState,
  fontFamily,
  fontSize,
  inTable,
  cellBgColor,
  applyCellBg,
  insertPageBreak,
  deleteCurrentPage,
  editorPageCount,
  insertRowAbove,
  insertRowBelow,
  deleteRow,
}: ToolbarProps) => (
  <div className='hre-toolbar flex items-center gap-1 px-3 py-1.5 flex-shrink-0 flex-wrap'>
    <Btn title='Undo (⌘Z)' onClick={() => exec('undo')}>
      <Undo />
    </Btn>
    <Btn title='Redo (⌘⇧Z)' onClick={() => exec('redo')}>
      <Redo />
    </Btn>

    <Sep />

    <Btn title='Insert page break at cursor — splits current page into two' onClick={insertPageBreak}>
      <InsertPageBreak />
    </Btn>
    <Btn title='Delete current page (requires at least two pages)' danger disabled={editorPageCount < 2} onClick={deleteCurrentPage}>
      <DeleteOutlined />
    </Btn>

    <Sep />

    <Dropdown
      title='Font family'
      value={fontFamily}
      width='190px'
      options={FONT_FAMILIES}
      onChange={v => exec('fontName', v)}
      onSaveSelection={saveSelection}
      onRestoreSelection={restoreSelection}
    />
    <Dropdown
      title='Font size'
      value={fontSize}
      width='62px'
      options={FONT_SIZE_OPTIONS}
      onChange={v => {
        if (v) applyFontSizeFromToolbar(Number(v));
      }}
      onSaveSelection={saveSelection}
      onRestoreSelection={restoreSelection}
    />

    <Sep />

    <Btn title='Bold (⌘B)' active={fmt.bold} onClick={() => exec('bold')}>
      <FormatBold />
    </Btn>
    <Btn title='Italic (⌘I)' active={fmt.italic} onClick={() => exec('italic')}>
      <FormatItalic />
    </Btn>
    <Btn title='Underline (⌘U)' active={fmt.underline} onClick={() => exec('underline')}>
      <FormatUnderlined />
    </Btn>
    <Btn title='Strikethrough' active={fmt.strikethrough} onClick={() => exec('strikeThrough')}>
      <FormatStrikethrough />
    </Btn>

    <Sep />

    <ColorWrap title='Text color' onMouseDownCapture={saveSelection}>
      <span className='hre-color-icon' aria-hidden='true'>
        A
      </span>
      <input
        type='color'
        className='hre-color-input'
        value={textColor}
        aria-label='Text color'
        onFocus={saveSelection}
        onChange={e => {
          restoreSelection();
          const c = e.target.value;
          setTextColorState(c);
          exec('foreColor', c);
        }}
      />
    </ColorWrap>

    <ColorWrap title='Highlight color' onMouseDownCapture={saveSelection}>
      <span className='hre-color-icon' aria-hidden='true'>
        ✦
      </span>
      <input
        type='color'
        className='hre-color-input'
        value={hlColor}
        aria-label='Highlight color'
        onFocus={saveSelection}
        onChange={e => {
          restoreSelection();
          const c = e.target.value;
          setHlColorState(c);
          exec('hiliteColor', c);
        }}
      />
    </ColorWrap>

    <Sep />

    <Btn title='Align left' active={fmt.justifyLeft} onClick={() => exec('justifyLeft')}>
      <FormatAlignLeft />
    </Btn>
    <Btn title='Align center' active={fmt.justifyCenter} onClick={() => exec('justifyCenter')}>
      <FormatAlignCenter />
    </Btn>
    <Btn title='Align right' active={fmt.justifyRight} onClick={() => exec('justifyRight')}>
      <FormatAlignRight />
    </Btn>
    <Btn title='Justify' active={fmt.justifyFull} onClick={() => exec('justifyFull')}>
      <FormatAlignJustify />
    </Btn>

    <Sep />

    <Btn title='Clear formatting' onClick={() => exec('removeFormat')}>
      <span className='text-[11px] font-semibold'>
        T<sub>x</sub>
      </span>
    </Btn>

    {inTable && (
      <>
        <Sep />
        <Btn title='Insert row above' onClick={insertRowAbove}>
          ↑ Row
        </Btn>
        <Btn title='Insert row below' onClick={insertRowBelow}>
          ↓ Row
        </Btn>
        <Btn title='Delete current row' danger onClick={deleteRow}>
          <DeleteOutlined />
          &nbsp;Row
        </Btn>
        <Sep />
        <ColorWrap title='Cell background color' onMouseDownCapture={saveSelection}>
          <FormatColorFill className='flex-shrink-0' aria-hidden />
          <input
            type='color'
            className='hre-color-input'
            value={cellBgColor}
            aria-label='Cell background color'
            onFocus={saveSelection}
            onChange={e => {
              restoreSelection();
              applyCellBg(e.target.value);
            }}
          />
        </ColorWrap>
      </>
    )}
  </div>
);
